/**
 * My Account — Phase 4
 * Route: /account
 *
 * Displays username + role (read-only).
 * Provides a Change Password form that calls PATCH /api/account/password.
 */

import { api, ApiError } from "../lib/api";
import { ensureAppTimezone, fmtDateTime } from "../lib/datetime";
import { renderHeader } from "../components/header";

interface Me {
  id: string;
  username: string;
  role: string;
  last_login: string | null;
  telegram_user_id: string | null;
  telegram_bot_username: string;
}

export async function renderAccountPage(root: HTMLElement): Promise<void> {
  // The page shows one timestamp; the zone has to be here before it renders.
  await ensureAppTimezone();
  root.appendChild(renderHeader());

  // Fetch current user info
  let user: Me | null = null;
  try {
    user = await api<Me>("/auth/me");
  } catch { /* show dashes */ }

  const shell = document.createElement("div");
  shell.className = "page-shell";
  shell.innerHTML = `
    <div class="page-content account-page">
      <h2>My Account</h2>

      <div class="account-card">
        <div class="field-group">
          <label>Username</label>
          <div class="account-username">${esc(user?.username ?? "—")}</div>
        </div>
        <div class="field-group">
          <label>Role</label>
          <div class="account-role">${esc(user?.role ?? "—")}</div>
        </div>
        ${user?.last_login ? `
        <div class="field-group">
          <label>Last Login</label>
          <div class="account-username">${esc(fmtDateTime(user.last_login))}</div>
        </div>` : ""}
      </div>

      ${buildBotCardHTML(user)}

      <div class="account-card">
        <h3>Change Password</h3>
        <form id="pw-form" novalidate>
          <div class="field-group">
            <label for="current-pw">Current Password</label>
            <input
              type="password"
              id="current-pw"
              autocomplete="current-password"
              placeholder="Enter current password"
            />
          </div>
          <div class="field-group">
            <label for="new-pw">New Password</label>
            <input
              type="password"
              id="new-pw"
              autocomplete="new-password"
              placeholder="Enter new password"
            />
          </div>
          <div class="field-group">
            <label for="confirm-pw">Confirm New Password</label>
            <input
              type="password"
              id="confirm-pw"
              autocomplete="new-password"
              placeholder="Re-enter new password"
            />
          </div>
          <div class="account-msg" id="pw-msg"></div>
          <button type="submit" class="primary" id="pw-submit">Update Password</button>
        </form>
      </div>
    </div>
  `;
  root.appendChild(shell);

  const form = root.querySelector<HTMLFormElement>("#pw-form")!;
  const msg = root.querySelector<HTMLElement>("#pw-msg")!;
  const submitBtn = root.querySelector<HTMLButtonElement>("#pw-submit")!;

  // ── Wire Telegram bot card ────────────────────────────────────────────────
  wireBotCard(root, user);

  form.addEventListener("submit", async e => {
    e.preventDefault();
    msg.textContent = "";
    msg.className = "account-msg";

    const currentPw = (root.querySelector<HTMLInputElement>("#current-pw")!).value.trim();
    const newPw = (root.querySelector<HTMLInputElement>("#new-pw")!).value;
    const confirmPw = (root.querySelector<HTMLInputElement>("#confirm-pw")!).value;

    if (!currentPw || !newPw || !confirmPw) {
      setMsg(msg, "All fields are required.", "error");
      return;
    }
    if (newPw !== confirmPw) {
      setMsg(msg, "New passwords do not match.", "error");
      return;
    }
    if (newPw.length < 8) {
      setMsg(msg, "New password must be at least 8 characters.", "error");
      return;
    }

    submitBtn.disabled = true;
    submitBtn.textContent = "Updating…";
    try {
      await api("/account/password", {
        method: "PATCH",
        body: { current_password: currentPw, new_password: newPw },
      });
      setMsg(msg, "Password updated successfully.", "success");
      form.reset();
    } catch (err) {
      const detail = err instanceof ApiError ? err.message : "Update failed. Please try again.";
      setMsg(msg, detail, "error");
    } finally {
      submitBtn.disabled = false;
      submitBtn.textContent = "Update Password";
    }
  });
}

function setMsg(el: HTMLElement, text: string, kind: "success" | "error"): void {
  el.textContent = text;
  el.className = `account-msg ${kind === "success" ? "success-text" : "error-text"}`;
}

// ── Telegram bot card ─────────────────────────────────────────────────────

function buildBotCardHTML(user: Me | null): string {
  const linked = !!user?.telegram_user_id;

  const inner = linked ? buildLinkedSection(user!) : buildSetupSection(user);

  return `
    <div class="account-card" id="tg-card">
      <h3>Kassandra Bot</h3>
      ${inner}
    </div>
  `;
}

function buildLinkedSection(user: Me): string {
  return `
    <div class="tg-linked-row">
      <span class="tg-linked-badge">✓ Linked</span>
      <span class="tg-linked-id">${esc(user.telegram_user_id!)}</span>
    </div>
    <p class="tg-linked-hint">You're all set. Message the bot anytime — or unlink below.</p>
    <div class="tg-try-list">
      <code>/help</code> — see what the bot can do<br>
      Paste a URL to store it as a signal<br>
      "send me today's brief" to get the daily brief
    </div>
    <div class="account-msg" id="tg-msg"></div>
    <button class="ap-action-btn ap-action-delete" id="tg-unlink" style="margin-top:var(--space-sm)">Unlink Telegram</button>
  `;
}

function buildSetupSection(user: Me | null): string {
  const botHandle = user?.telegram_bot_username
    ? `@${esc(user.telegram_bot_username)}`
    : "the Kassandra bot (ask your admin for the handle)";

  return `
    <ol class="tg-steps">
      <li>
        <strong>Find the bot</strong><br>
        Open Telegram and search for <strong>${botHandle}</strong>.
      </li>
      <li>
        <strong>Get your Telegram ID</strong><br>
        Send <code>/start</code> to the bot. It will reply with your numeric Telegram User ID.
        <blockquote class="tg-example">
          Your Telegram ID is 123456789.<br>
          Share this with your Kassandra admin to get access.
        </blockquote>
      </li>
      <li>
        <strong>Enter your Telegram ID below</strong><br>
        Paste the number from Step 2 and click <em>Save</em> — linking is instant.
        <div class="tg-field-row" style="margin-top:var(--space-sm)">
          <input
            type="text"
            id="tg-id-input"
            class="tg-id-input"
            placeholder="e.g. 123456789"
            inputmode="numeric"
            autocomplete="off"
          />
          <button class="primary" id="tg-save">Save</button>
        </div>
        <div class="account-msg" id="tg-msg"></div>
      </li>
      <li>
        <strong>Try it out</strong><br>
        <div class="tg-try-list">
          <code>/help</code> — see what the bot can do<br>
          Paste a URL to store it as a signal<br>
          "what did we collect on AI governance this week?" to search<br>
          "send me today's brief" to get the daily brief
        </div>
      </li>
    </ol>
  `;
}

function wireBotCard(root: HTMLElement, user: Me | null): void {
  const msg = root.querySelector<HTMLElement>("#tg-msg");
  if (!msg) return;

  // ── Save (link) ───────────────────────────────────────────────────────────
  const saveBtn = root.querySelector<HTMLButtonElement>("#tg-save");
  const input = root.querySelector<HTMLInputElement>("#tg-id-input");
  if (saveBtn && input) {
    saveBtn.addEventListener("click", async () => {
      msg.textContent = "";
      msg.className = "account-msg";
      const val = input.value.trim();
      if (!val) { setMsg(msg, "Enter your Telegram User ID.", "error"); return; }
      if (!/^\d+$/.test(val)) { setMsg(msg, "Telegram User IDs are numeric only.", "error"); return; }
      saveBtn.disabled = true;
      saveBtn.textContent = "Saving…";
      try {
        await api("/account/telegram", { method: "PATCH", body: { telegram_user_id: val } });
        // Rebuild card in linked state
        if (user) user.telegram_user_id = val;
        const card = root.querySelector<HTMLElement>("#tg-card");
        if (card && user) card.outerHTML = buildBotCardHTML(user);
        // Re-wire the new card (now in linked state — only has unlink button)
        wireBotCard(root, user);
      } catch (err) {
        setMsg(msg, err instanceof ApiError ? err.message : "Failed to save.", "error");
        saveBtn.disabled = false;
        saveBtn.textContent = "Save";
      }
    });
  }

  // ── Unlink ────────────────────────────────────────────────────────────────
  const unlinkBtn = root.querySelector<HTMLButtonElement>("#tg-unlink");
  if (unlinkBtn) {
    unlinkBtn.addEventListener("click", async () => {
      if (!confirm("Unlink your Telegram account? You can re-link at any time.")) return;
      unlinkBtn.disabled = true;
      unlinkBtn.textContent = "Unlinking…";
      try {
        await api("/account/telegram", { method: "PATCH", body: { telegram_user_id: null } });
        if (user) user.telegram_user_id = null;
        const card = root.querySelector<HTMLElement>("#tg-card");
        if (card && user) card.outerHTML = buildBotCardHTML(user);
        wireBotCard(root, user);
      } catch (err) {
        setMsg(msg, err instanceof ApiError ? err.message : "Failed to unlink.", "error");
        unlinkBtn.disabled = false;
        unlinkBtn.textContent = "Unlink Telegram";
      }
    });
  }
}

function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
